import { useEffect } from 'react';
import { useLocation, useNavigationType, type NavigationType } from 'react-router-dom';
import { jsonCodec, readPref, UI_PREFS, withCodec, writePref } from '@/lib/ui-prefs';

/**
 * Window scroll for app pages (PageLayout): a new page opens at the top, and Back, Forward or a
 * reload puts you where you were on that history entry once the page is tall enough again (its data
 * has loaded), unless you scroll first. Pages that scroll inside their own panes don't use it; the
 * Board remembers its columns itself (`pages/tasks/board-scroll.ts`).
 */

type ScrollEntries = [entryKey: string, y: number][];

/** Kept for this tab, in sessionStorage. */
const PAGE_SCROLL = withCodec(
  UI_PREFS.appPageScroll,
  jsonCodec<ScrollEntries>([], (value) =>
    Array.isArray(value)
      ? value.filter(
          (item): item is [string, number] =>
            Array.isArray(item) && typeof item[0] === 'string' && typeof item[1] === 'number'
        )
      : undefined
  )
);

/** History entries remembered; the oldest go first. */
const MAX_ENTRIES = 100;
/** How long Back waits for the page to grow tall enough before scrolling as far as it can. */
const RESTORE_WAIT_MS = 4000;
/** Any of these before the restore means the person has moved on: leave the scroll alone. */
const INPUT_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

let positions: Map<string, number> | null = null;
/**
 * The page last shown, and the navigation being handled with the page before it. Module-wide, so a
 * layout that remounts doesn't forget, and StrictMode's second run of the effect for the same
 * navigation decides the same way.
 */
let shownPage: string | undefined;
let navigation: { key: string; previousPage: string | undefined } | null = null;
/** Stops the restore still waiting for the page to grow, when another page opens. */
let cancelRestore: (() => void) | null = null;
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function readPositions(): Map<string, number> {
  if (positions) return positions;
  positions = new Map(readPref(PAGE_SCROLL));
  return positions;
}

/** Remember where a history entry is scrolled to (kept for this tab, in sessionStorage). */
export function rememberScroll(entryKey: string, y: number): void {
  const map = readPositions();
  map.delete(entryKey);
  map.set(entryKey, Math.max(0, Math.round(y)));
  while (map.size > MAX_ENTRIES) map.delete(map.keys().next().value as string);
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    saveTimer = null;
    // Storage full or blocked: memory still has it.
    writePref(PAGE_SCROLL, [...map]);
  }, 250);
}

/**
 * The history entry on screen: React Router's index in history.state, with the path (an index is
 * reused once you go back and open something else). Unlike location.key it survives the URL
 * replaces filters make, and it's read when needed, so a scroll during a page change lands on the
 * entry it belongs to.
 */
export function historyEntryKey(): string {
  const state = window.history.state as { idx?: unknown } | null;
  const idx = typeof state?.idx === 'number' ? state.idx : 0;
  return `${idx}:${window.location.pathname}`;
}

export function savedScroll(entryKey: string): number | undefined {
  return readPositions().get(entryKey);
}

/** For tests. */
export function forgetScrollPositions(): void {
  positions = new Map();
  shownPage = undefined;
  navigation = null;
}

export type ScrollAction = { kind: 'none' } | { kind: 'top' } | { kind: 'restore'; y: number };

/**
 * What to do with the window scroll after a navigation. `previousPage` is undefined on the first
 * render (a fresh tab or a reload), which the router reports as a POP.
 */
export function scrollActionFor({
  navigationType,
  previousPage,
  page,
  saved,
}: {
  navigationType: `${NavigationType}`;
  previousPage: string | undefined;
  page: string;
  saved: number | undefined;
}): ScrollAction {
  const samePage = previousPage === page;
  if (navigationType === 'POP') {
    if (saved !== undefined && saved > 0) {
      return samePage ? { kind: 'none' } : { kind: 'restore', y: saved };
    }
    return samePage || previousPage === undefined ? { kind: 'none' } : { kind: 'top' };
  }
  // PUSH or REPLACE: filters and tabs on the same page replace the URL and keep their place.
  return samePage ? { kind: 'none' } : { kind: 'top' };
}

/**
 * Scroll to `y` once the document is tall enough, waiting up to `waitMs` for content to arrive.
 * Stops if the person scrolls, clicks or types first. Returns the cancel.
 */
export function restoreScrollWhenReady(y: number, waitMs = RESTORE_WAIT_MS): () => void {
  const root = document.documentElement;
  const maxScroll = () => root.scrollHeight - window.innerHeight;
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    observer?.disconnect();
    clearTimeout(timer);
    for (const type of INPUT_EVENTS) window.removeEventListener(type, finish, true);
  };
  const tryRestore = () => {
    if (done) return;
    if (maxScroll() >= y) {
      window.scrollTo(0, y);
      finish();
    }
  };
  for (const type of INPUT_EVENTS) window.addEventListener(type, finish, { capture: true, passive: true });
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(tryRestore);
  observer?.observe(document.body);
  const timer = setTimeout(() => {
    if (done) return;
    // The page never grew that tall (fewer rows now): go as far as it goes.
    window.scrollTo(0, Math.min(y, Math.max(0, maxScroll())));
    finish();
  }, waitMs);
  tryRestore();
  return finish;
}

/** Window scroll for the page under `page` (PageLayout's page key); off for pages with their own panes. */
export function usePageScroll(page: string, enabled: boolean): void {
  const location = useLocation();
  const navigationType = useNavigationType();

  // The app restores scroll itself, once the data is there.
  useEffect(() => {
    if (!('scrollRestoration' in window.history)) return undefined;
    const before = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => {
      window.history.scrollRestoration = before;
    };
  }, []);

  // Remember this history entry's scroll as it changes.
  useEffect(() => {
    if (!enabled) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        rememberScroll(historyEntryKey(), window.scrollY);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [enabled]);

  useEffect(() => {
    if (navigation?.key !== location.key) {
      navigation = { key: location.key, previousPage: shownPage };
    }
    shownPage = page;
    const action = scrollActionFor({
      navigationType,
      previousPage: navigation.previousPage,
      page,
      saved: savedScroll(historyEntryKey()),
    });
    if (!enabled || action.kind === 'none') return;
    // Not the effect's cleanup: a page writing its filters into the URL as it opens (a replace)
    // must not stop the restore it's waiting on.
    cancelRestore?.();
    cancelRestore = null;
    if (action.kind === 'top') window.scrollTo(0, 0);
    if (action.kind === 'restore') cancelRestore = restoreScrollWhenReady(action.y);
    // location.key: every navigation, including to the same path.
  }, [enabled, location.key, navigationType, page]);
}
